import React, { useState, useRef, useEffect } from 'react';
import { PaperAirplaneIcon, StopIcon } from '@heroicons/react/24/solid';
import { cn } from '../../../lib/utils';

interface ChatInputProps {
    onSend: (message: string) => void;
    isLoading: boolean;
    onStop?: () => void; // Optional if we implement stop generation
}

const ChatInput: React.FC<ChatInputProps> = ({ onSend, isLoading, onStop }) => {
    const [input, setInput] = useState('');
    const inputRef = useRef<HTMLInputElement>(null);

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (!input.trim() || isLoading) return;
        onSend(input);
        setInput('');
    };

    // Auto-focus on mount and after sending
    useEffect(() => {
        if (!isLoading) {
            inputRef.current?.focus();
        }
    }, [isLoading]);

    return (
        <div className="bg-white border-t border-gray-100 p-4 pb-6">
            <div className="max-w-3xl mx-auto relative group">
                {/* Glow effect */}
                <div className="absolute -inset-1 bg-gradient-to-r from-[#3B3E81] to-[#E23338] rounded-2xl blur opacity-15 group-focus-within:opacity-20 transition duration-500"></div>

                <form
                    onSubmit={handleSubmit}
                    className="relative bg-gray-50 flex items-center p-2 rounded-2xl border border-gray-200 focus-within:ring-2 focus-within:ring-indigo-100 focus-within:border-indigo-300 transition-all shadow-sm"
                >
                    <input
                        ref={inputRef}
                        type="text"
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        placeholder="Message MRC AI..."
                        className="flex-1 bg-transparent border-none outline-none px-4 py-3 text-gray-700 placeholder-gray-400 w-full resize-none"
                        disabled={isLoading}
                    />

                    <button
                        type="submit"
                        disabled={!input.trim() && !isLoading}
                        className={cn(
                            "p-2.5 rounded-xl transition-all duration-200 flex-shrink-0",
                            input.trim() && !isLoading
                                ? "bg-primary text-primary-foreground shadow-md hover:opacity-90 transform hover:scale-105 active:scale-95"
                                : "bg-gray-200 text-gray-400 cursor-not-allowed"
                        )}
                    >
                        {isLoading ? (
                            <div className="w-5 h-5 border-2 border-current border-t-transparent rounded-full animate-spin" />
                        ) : (
                            <PaperAirplaneIcon className="w-5 h-5" />
                        )}
                    </button>
                </form>

                <div className="mt-2 text-center text-xs text-gray-400">
                    AI can make mistakes. Please verify important information.
                </div>
            </div>
        </div>
    );
};

export default ChatInput;
